import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { IRelatedAsset, RelatedAsset } from '@app/core/model/entities/asset/asset';
import { AssetsRelatedEntity } from '@app/core/model/entities/asset/assets-related-entity';
import { Document } from '@app/core/model/entities/document/document';
import { Entity, IRelatedEntity, RelatedEntity } from '@app/core/model/entities/entity';
import { getValue, hasProperty, setValue } from '@app/shared/extra/utils';
import { Exclude, Expose, Type } from 'class-transformer';

export class Space extends Entity implements AssetsRelatedEntity {
  public objectType = EntityTypeEnum.SPACE;

  public identifier: string;
  public assetId: string;

  @Expose({name: 'parentPathList'})
  public parentPath: string[];

  @Type(() => Document)
  public documents: Document[] = [];

  public dataDate: string;

  // Parent spaces' names
  @Exclude() public hierarchy: string[];

  // Whether the Space has children
  @Exclude() public hasChildren: boolean;

  @Expose()
  public toString(): string {
    return this.computedProperties?.['displayName'] ?? `${this?.identifier} - ${this?.name}`;
  }

  /**
   * Transforms this Space to a RelatedSpace.
   */
  public toRelatedSpace(): RelatedSpace {
    return new RelatedSpace({
      id: this.id,
      name: this.name,
      identifier: this.identifier,
      parentPath: this.parentPath,
      displayName: this.computedProperties?.['displayName']
    });
  }

  /**
   * Get the related asset inside the Space computed properties.
   * @return List of related assets
   */
  @Expose()
  public get relatedAssets(): RelatedAsset[] {
    return this.computedProperties?.['asset']
        ?.let((asset: IRelatedAsset) => [new RelatedAsset(asset)])
      ?? [];
  }

  protected static inputMapping = {
    asset: {input: ['computedProperties', 'asset', 'id'], output: 'assetId'}
  };

  /**
   * Some GraphQL inputs require different keys than those available in the Form State Service
   * since those keys correspond to field codes, so we need to replace them accordingly
   * @param obj the Graphql input object
   */
  public static transformPropertiesForInput(obj: Record<string, unknown>): void {
    for (const inputMappingElement in Space.inputMapping) {
      // Transform input, mapping keys as defined in the inputMapping above
      if (hasProperty(obj, Space.inputMapping[inputMappingElement].input)) {
        const source = getValue(obj, Space.inputMapping[inputMappingElement].input);
        setValue(obj, [Space.inputMapping[inputMappingElement].output], source);
      }
    }

    // Clean up by removing older keys
    for (const inputMappingElement in Space.inputMapping) {
      delete obj[Space.inputMapping[inputMappingElement].input.first()];
    }
  }
}

export type IRelatedSpace = IRelatedEntity & { parentPath: string | string[] };

export class RelatedSpace extends RelatedEntity implements IRelatedSpace {
  public parentPath: string[];

  constructor(relatedSpace: IRelatedSpace) {
    super(relatedSpace);
    this.parentPath = typeof relatedSpace.parentPath === 'string' ? JSON.parse(relatedSpace.parentPath) : relatedSpace.parentPath;
  }
}

export interface SpaceInput {
  name?: string;
  properties?: Record<string, any>;
  dataDate?: string;
  assetId?: string;
  parentPath?: string[];
}
